import { useEffect, useMemo, useState } from 'react';
import { AppHomeBannerFallback } from '@/utils/constants';
// import { AppWelcomebannerFallback } from '@/utils/constants';
import { useGetBannersQuery } from '@/store/api/bannerApi';
import { BannerResponse } from '@/types/banner';

const pickBanners = (src: any, key: string) =>
  src?.[key]
    ?.map((b: any) => ({ type: b.type, image: b.image, size: b.size }))
    .filter((banner: { image?: string }) => Boolean(banner.image)) || [];

export function useHomeBanner() {
  const { data: banners, isLoading: isBannersLoading } = useGetBannersQuery(undefined);

  // show Welcome Banner only once
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);

  // banner groups
  const bannerGroups = banners?.data ?? banners ?? ({} as BannerResponse);
  const { AppBanner } = bannerGroups;
  const appHomeBannerImages = pickBanners(AppBanner, 'HomeBanner');
  const appWelcomeBannerImages = useMemo(
    () => pickBanners(AppBanner, 'WellcomeBanner'),
    [AppBanner]
  );

  // Start downloading the welcome banner as soon as its URL is known, so the
  // popup (shown after login) doesn't wait on the image download.
  useEffect(() => {
    appWelcomeBannerImages.forEach((banner: { image?: string }) => {
      if (!banner.image) return;
      const preload = new Image();
      preload.src = banner.image;
    });
  }, [appWelcomeBannerImages]);

  // home banner
  // NOTE: forced to always use the local provider banners (AppHomeBannerFallback)
  // instead of `appHomeBannerImages` from the CMS/API — the live backend
  // currently only has a placeholder test banner, so API-driven home
  // banners are disabled for now. Re-enable
  // `appHomeBannerImages.length > 0 ? appHomeBannerImages : AppHomeBannerFallback`
  // once real banners are managed from the backend.
  const displayHomeBanner = useMemo(() => {
    if (isBannersLoading) return [];

    return AppHomeBannerFallback;
  }, [isBannersLoading]);

  // welcome banner
  const displayWelcomeBanner = useMemo(() => {
    if (isBannersLoading) return [];

    return appWelcomeBannerImages;
    // return appWelcomeBannerImages.length > 0 ? appWelcomeBannerImages : AppWelcomebannerFallback;
  }, [appWelcomeBannerImages, isBannersLoading]);

  // welcome banner one-time logic
  useEffect(() => {
    const flag = localStorage.getItem('showWelcomeBanner');

    if (flag === 'true') {
      setShowWelcomeBanner(true);
      localStorage.setItem('showWelcomeBanner', 'false');
    }
  }, []);

  return {
    showWelcomeBanner,
    setShowWelcomeBanner,
    displayHomeBanner,
    displayWelcomeBanner,
    isBannersLoading,
  };
}
